/**
 * Tournament service
 * Handles all tournament-related API calls
 */

import { API_ENDPOINTS } from './endpoints';
import { fetchApi } from '@/lib/utils/api';
import type { PublicTournament, Tournament } from '@/lib/types/auction';
import type { PaginatedResponse } from '@/lib/types/api';
import type { Enrollment } from '@/lib/types/enrollment';
import { showSuccessToast } from '@/lib/utils/toast';

interface CreateTournamentInput {
  name: string;
  type: string;
  sport: string;
  description?: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  totalTeams: number;
  maxPlayersPerTeam: number;
  baseBudget: number;
}

interface UpdateTournamentInput extends Partial<CreateTournamentInput> {
  status?: Tournament['status'];
}

interface GetPublicTournamentsParams {
  page?: number;
  limit?: number;
  status?: Tournament['status'];
}

export const tournamentService = {
  /**
   * Create new tournament
   */
  async create(data: CreateTournamentInput): Promise<Tournament> {
    const response = await fetchApi<Tournament>(
      API_ENDPOINTS.TOURNAMENTS.BASE,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    showSuccessToast('tournament', 'create');
    return response.result;
  },

  /**
   * Update tournament
   */
  async update(id: string, data: UpdateTournamentInput): Promise<Tournament> {
    const response = await fetchApi<Tournament>(
      API_ENDPOINTS.TOURNAMENTS.PROFILE(id),
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
    showSuccessToast('tournament', 'update');
    return response.result;
  },

  /**
   * Get tournament by ID
   */
  async getById(id: string): Promise<Tournament> {
    const response = await fetchApi<Tournament>(
      API_ENDPOINTS.TOURNAMENTS.PROFILE(id),
      {
        requireAuth: false,
      }
    );
    return response.result;
  },

  /**
   * Get my tournaments with pagination
   */
  async getMyTournaments(params?: {
    page?: number;
    limit?: number;
    status?: Tournament['status'];
  }): Promise<Tournament[]> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);

    const response = await fetchApi<PaginatedResponse<Tournament>>(
      `${API_ENDPOINTS.TOURNAMENTS.BASE}?${searchParams.toString()}`
    );
    return response.result.data;
  },

  /**
   * Get public tournaments with pagination
   */
  async getPublicTournaments(
    params?: GetPublicTournamentsParams
  ): Promise<Tournament[]> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);

    const response = await fetchApi<PaginatedResponse<Tournament>>(
      `${API_ENDPOINTS.TOURNAMENTS.PUBLIC}?${searchParams.toString()}`,
      {
        requireAuth: false,
      }
    );
    return response.result.data;
  },

  /**
   * Get public tournaments sitemap
   */
  async getPublicTournamentsSitemap(): Promise<PublicTournament[]> {
    const response = await fetchApi<{ result: PublicTournament[] }>(
      API_ENDPOINTS.TOURNAMENTS.SITEMAP,
      { requireAuth: false }
    );
    return Array.isArray(response.result) ? response.result : [];
  },

  /**
   * Get active tournament
   */
  async getActive(): Promise<Tournament> {
    const response = await fetchApi<Tournament>(
      API_ENDPOINTS.TOURNAMENTS.ACTIVE
    );
    return response.result;
  },

  /**
   * Delete tournament
   */
  async delete(id: string): Promise<void> {
    await fetchApi<void>(API_ENDPOINTS.TOURNAMENTS.PROFILE(id), {
      method: 'DELETE',
    });
    showSuccessToast('tournament', 'delete');
  },

  /**
   * Start auction
   */
  async start(id: string): Promise<Tournament> {
    const response = await fetchApi<Tournament>(
      API_ENDPOINTS.TOURNAMENTS.START(id),
      {
        method: 'POST',
      }
    );
    return response.result;
  },

  /**
   * End auction
   */
  async end(id: string): Promise<Tournament> {
    const response = await fetchApi<Tournament>(
      API_ENDPOINTS.TOURNAMENTS.END(id),
      {
        method: 'POST',
      }
    );
    return response.result;
  },

  /**
   * Open registration
   */
  async openRegistration(id: string): Promise<Tournament> {
    return this.update(id, { status: 'REGISTRATION' });
  },

  /**
   * Close registration
   */
  async closeRegistration(id: string): Promise<Tournament> {
    return this.update(id, { status: 'UPCOMING' });
  },

  /**
   * Get current user's enrollment status for a tournament
   */
  async getMyEnrollment(tournamentId: string): Promise<Enrollment> {
    const response = await fetchApi<Enrollment>(
      API_ENDPOINTS.TOURNAMENTS.ENROLLMENT(tournamentId)
    );
    return response.result;
  },

  /**
   * Generate Player Reports
   */
  async generatePlayerReports(id: string): Promise<{ message: string }> {
    const response = await fetchApi<{ message: string }>(
      API_ENDPOINTS.TOURNAMENTS.GENERATE_PLAYER_REPORTS(id)
    );
    return response.result;
  },
};
